import {Injectable} from '@angular/core';
import jsQR from 'jsqr';

export interface CameraScannerSession {
  stop: () => void;
}

@Injectable({providedIn: 'root'})
export class QrCameraScannerService {
  isSupported(): boolean {
    return typeof navigator !== 'undefined'
      && !!navigator.mediaDevices?.getUserMedia
      && typeof window !== 'undefined';
  }

  async start(video: HTMLVideoElement, onScan: (value: string) => void): Promise<CameraScannerSession> {
    if (!this.isSupported()) {
      throw new Error('เบราว์เซอร์นี้ไม่รองรับการสแกน QR ผ่านกล้อง');
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: {ideal: 'environment'}
      },
      audio: false
    });

    video.srcObject = stream;
    await video.play();

    const hasBarcodeDetector = typeof (window as any).BarcodeDetector !== 'undefined';
    const detector = hasBarcodeDetector ? new (window as any).BarcodeDetector({formats: ['qr_code']}) : null;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', {willReadFrequently: true});
    let lastScanAt = 0;

    let active = true;
    let rafId = 0;

    const stop = (): void => {
      if (!active) {
        return;
      }
      active = false;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      const currentStream = video.srcObject as MediaStream | null;
      currentStream?.getTracks().forEach((track) => track.stop());
      video.srcObject = null;
    };

    const loop = async (): Promise<void> => {
      if (!active) {
        return;
      }

      try {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          if (detector) {
            const codes = await detector.detect(video);
            if (codes.length > 0 && codes[0].rawValue) {
              onScan(codes[0].rawValue);
              stop();
              return;
            }
          } else if (context && video.videoWidth > 0 && video.videoHeight > 0) {
            const now = Date.now();
            if (now - lastScanAt >= 160) {
              lastScanAt = now;
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              context.drawImage(video, 0, 0, canvas.width, canvas.height);
              const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
              const result = jsQR(imageData.data, imageData.width, imageData.height, {
                inversionAttempts: 'attemptBoth'
              });
              if (result?.data) {
                onScan(result.data);
                stop();
                return;
              }
            }
          }
        }
      } catch {
        // Ignore scan errors during frames and keep trying until stop.
      }

      rafId = requestAnimationFrame(() => {
        void loop();
      });
    };

    void loop();

    return {stop};
  }
}
